import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/api';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import "../../styles/CourseFormModalTabs.css";
import toast from "react-hot-toast";

function CourseFormModalTabs({ show, onClose, courseData, onCourseSaved, setNotification }) {
    // --- ESTADOS DE CURSO ---
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState('Rascunho');
    const [coverImageUrl, setCoverImageUrl] = useState('');
    const [availableFormats, setAvailableFormats] = useState([]);
    const [price, setPrice] = useState('');
    const [paymentType, setPaymentType] = useState('Gratuito');

    // --- ESTADOS PARA DEGUSTAÇÃO ---
    const [hasPreview, setHasPreview] = useState(false);
    const [previewVideoUrl, setPreviewVideoUrl] = useState('');
    const [previewSeconds, setPreviewSeconds] = useState(30);
    const [isPreviewUploading, setIsPreviewUploading] = useState(false);

    // --- ESTADOS DE MÓDULO E CONTROLE ---
    const [modules, setModules] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [isCoverUploading, setIsCoverUploading] = useState(false);

    const isEdit = !!courseData;
    const optionLetters = ['A', 'B', 'C', 'D'];
    const contentTypes = ['PDF', 'Word', 'YouTube', 'HTML', 'Video', 'PPTX'];

    const BACKEND_URL = "https://imagensdaface.com.br";

    // ==========================================
    // MAPEADORES DE TIPO (FRONT <-> BANCO)
    // ==========================================
        const typeToDB = (type) => {
            const map = {
                'HTML': 'html',
                'Video': 'video',     // Alinhado com o Backend
                'YouTube': 'youtube', // Alinhado com o Backend (minúsculo)
                'PDF': 'pdf',
                'PPTX': 'pptx',
                'Word': 'word'
            };
            return map[type] || 'html';
        };

    const dbToFront = (dbType) => {
        if (!dbType) return 'HTML';
        
        const map = {
            'texto': 'HTML',
            'html': 'HTML',
            'video_upload': 'Video',
            'video': 'Video',
            'video_embed': 'YouTube', // Tradução correta do banco para o Front
            'youtube': 'YouTube',     // Segurança caso venha minúsculo
            'pdf': 'PDF',
            'pptx': 'PPTX',
            'download': 'Word',
            'word': 'Word'
        };
        
        return map[dbType.toLowerCase()] || 'HTML';
    };

    const getFullUrl = (path) => {
        if (!path) return "";
        if (path.startsWith("http")) return path;
        return `${BACKEND_URL}/${path.replace(/^\//, "")}`;
    };

    // ==========================================
    // PERSISTÊNCIA (Rascunho LocalStorage)
    // ==========================================
    useEffect(() => {
        if (show && !isEdit) {
            const draft = {
                title, description, status, coverImageUrl, availableFormats,
                price, paymentType, hasPreview, previewVideoUrl, previewSeconds, modules
            };
            localStorage.setItem('course_form_draft', JSON.stringify(draft));
        }
    }, [title, description, status, coverImageUrl, availableFormats, price, paymentType, hasPreview, previewVideoUrl, previewSeconds, modules, show, isEdit]);

    useEffect(() => {
        if (show && !isEdit) {
            const savedDraft = localStorage.getItem('course_form_draft');
            if (savedDraft) {
                const draft = JSON.parse(savedDraft);
                if (title === '' && (modules.length === 0 || (modules.length === 1 && !modules[0].content_value))) {
                    setTitle(draft.title || '');
                    setDescription(draft.description || '');
                    setStatus(draft.status || 'Rascunho');
                    setCoverImageUrl(draft.coverImageUrl || '');
                    setAvailableFormats(draft.availableFormats || []);
                    setPrice(draft.price || '');
                    setPaymentType(draft.paymentType || 'Gratuito');
                    setHasPreview(draft.hasPreview || false);
                    setPreviewVideoUrl(draft.previewVideoUrl || '');
                    setPreviewSeconds(draft.previewSeconds || 30);
                    if (draft.modules && draft.modules.length > 0) setModules(draft.modules);
                }
            } else if (modules.length === 0) {
                setModules(generateEmptyModules(1));
            }
        }
    }, [show, isEdit]);

    const clearDraft = () => localStorage.removeItem('course_form_draft');

    const generateEmptyQuestion = () => ({
        question_text: '',
        options: ['', '', '', ''],
        correct_option_index: 0,
    });

    const handleQuizToggle = (index, value) => {
        setModules(prev => {
            const copy = [...prev];
            copy[index] = { ...copy[index], has_quiz: value };
            if (value && (!copy[index].quiz_questions || copy[index].quiz_questions.length === 0)) {
                copy[index].quiz_questions = [generateEmptyQuestion()];
            }
            return copy;
        });
    };

    const handleQuestionFieldChange = (moduleIndex, questionIndex, field, value) => {
        setModules(prev => {
            const newModules = [...prev];
            const targetModule = { ...newModules[moduleIndex] };
            const currentQuestions = targetModule.quiz_questions ? [...targetModule.quiz_questions] : [];
            const targetQuestion = { ...currentQuestions[questionIndex] };

            if (field === 'option_text') {
                const newOptions = [...targetQuestion.options];
                newOptions[value.optionIndex] = value.text;
                targetQuestion.options = newOptions;
            } else if (field === 'correct_option_index') {
                targetQuestion.correct_option_index = Number(value);
            } else {
                targetQuestion[field] = value;
            }

            currentQuestions[questionIndex] = targetQuestion;
            targetModule.quiz_questions = currentQuestions;
            newModules[moduleIndex] = targetModule;
            return newModules;
        });
    };

    const handleAddQuestion = (moduleIndex) => {
        setModules(prev => {
            const copy = [...prev];
            copy[moduleIndex].quiz_questions = [...copy[moduleIndex].quiz_questions, generateEmptyQuestion()];
            return copy;
        });
    };

    const handleRemoveQuestion = (moduleIndex, questionIndex) => {
        setModules(prev => {
            const copy = [...prev];
            if (copy[moduleIndex].quiz_questions.length > 1) {
                copy[moduleIndex].quiz_questions = copy[moduleIndex].quiz_questions.filter((_, i) => i !== questionIndex);
            } else {
                copy[moduleIndex].has_quiz = false;
                copy[moduleIndex].quiz_questions = [];
            }
            return copy;
        });
    };

    const generateEmptyModules = useCallback((count, startIndex = 0) =>
        Array.from({ length: count }, (_, i) => ({
            id: `temp-${Date.now()}-${i}`,
            title: `Módulo ${startIndex + i + 1}`,
            description: '',
            order_index: startIndex + i + 1,
            content_type: 'HTML',
            content_value: '',
            isUploading: false,
            has_quiz: false,
            quiz_questions: [],
            is_required: false,
        }))
    , []);

    const addModule = () => {
        setModules(prev => {
            const next = [...prev, ...generateEmptyModules(1, prev.length)];
            setActiveTab(next.length - 1);
            return next;
        });
    };

    const removeModule = async (moduleId) => {
        const loadingToast = toast.loading("Excluindo módulo...");
        try {
            if (!String(moduleId).includes('temp')) {
                await api.delete(`/courses/modules/${moduleId}`);
            }
            setModules(prev => {
                const removedIndex = prev.findIndex(m => m.id === moduleId);
                const next = prev.filter(m => m.id !== moduleId);
                setActiveTab(current => {
                    if (removedIndex < current) return current - 1;
                    if (removedIndex === current) return Math.max(0, current - 1);
                    return current;
                });
                return next.map((m, i) => ({ ...m, order_index: i + 1 }));
            });
            toast.success("Módulo removido com sucesso", { id: loadingToast });
        } catch (err) {
            toast.error("Erro ao excluir módulo", { id: loadingToast });
        }
    };

    const handleModuleField = (index, field, value) => {
        setModules(prev => {
            const copy = [...prev];
            if (field === 'content_type' && copy[index].content_type !== value) {
                copy[index] = { ...copy[index], [field]: value, content_value: '' };
            } else {
                copy[index] = { ...copy[index], [field]: value };
            }
            return copy;
        });
    };

    const handleCoverUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsCoverUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await api.post('/upload/cover', formData);
            setCoverImageUrl(response.data.file.url);
        } catch (err) {
            toast.error('Erro ao enviar capa');
        } finally {
            setIsCoverUploading(false);
        }
    };

    const handlePreviewUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsPreviewUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await api.post('/upload/lesson', formData);
            setPreviewVideoUrl(response.data.file.url);
        } catch (err) {
            toast.error('Erro ao enviar preview');
        } finally {
            setIsPreviewUploading(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        handleModuleField(activeTab, 'isUploading', true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await api.post('/upload/lesson', formData);
            handleModuleField(activeTab, 'content_value', response.data.file.url);
        } catch (err) {
            toast.error('Erro ao enviar arquivo');
        } finally {
            handleModuleField(activeTab, 'isUploading', false);
        }
    };

  const fetchCourseData = useCallback(async () => {
        if (!isEdit) return;
        setDataLoading(true);
        try {
            const response = await api.get(`/courses/${courseData.id}`);
            const full = response.data.course || response.data;
            
            setTitle(full.title || '');
            setDescription(full.description || '');
            setStatus(full.status || 'Rascunho');
            setCoverImageUrl(full.cover_image_url || '');
            setPrice(full.price ?? '');
            setPaymentType(Number(full.price) > 0 ? 'Pago' : 'Gratuito');
            setPreviewVideoUrl(full.preview_video_url || '');
            setPreviewSeconds(full.preview_seconds || 30);
            setHasPreview(!!full.preview_video_url);

            const modulesResponse = await api.get(`/courses/${courseData.id}/modules`);
            
            // 🚩 MAPEAMENTO CORRIGIDO:
            const loadedModules = modulesResponse.data.modules.map((m) => {
                // Pega a primeira aula (lesson) se existir
                const firstLesson = m.lessons && m.lessons.length > 0 ? m.lessons[0] : null;
                
                // Extrai o tipo bruto do banco (ex: 'video_embed', 'html', 'texto')
                const rawType = firstLesson?.content_type || 'html';

                return {
                    id: m.id,
                    title: m.title || '',
                    description: m.description || '',
                    order_index: m.module_order || 1,
                    // 🚩 TRADUÇÃO: Usa dbToFront para converter 'video_embed' em 'YouTube'
                    content_type: dbToFront(rawType),
                    // 🚩 VALOR: Aqui vai o link do YouTube ou o conteúdo HTML
                    content_value: firstLesson ? firstLesson.content : '',
                    isUploading: false,
                    has_quiz: !!m.has_quiz,
                    quiz_questions: Array.isArray(m.quiz_questions) ? m.quiz_questions : [],
                    is_required: !!m.is_required,
                };
            });

            // Define os módulos ou gera um vazio se o curso estiver zerado
            setModules(loadedModules.length > 0 ? loadedModules : generateEmptyModules(1));
            
            // Garante que a primeira aba esteja ativa ao abrir para edição
            setActiveTab(0); 

        } catch (err) { 
            console.error("Erro ao carregar módulos:", err);
            toast.error("Erro ao carregar dados do curso");
        } finally { 
            setDataLoading(false); 
        }
    }, [courseData, isEdit, generateEmptyModules]);
    
    

    useEffect(() => {
        if (show && isEdit) fetchCourseData();
    }, [show, isEdit, fetchCourseData]);

    const UploadDropzone = ({ accept, onFileSelect, contentType, isUploading }) => {
        const [isDragging, setIsDragging] = useState(false);
        const inputRef = React.useRef(null);
        const handleDrag = (e) => {
            e.preventDefault(); e.stopPropagation();
            if (e.type === "dragenter" || e.type === "dragover") setIsDragging(true);
            else if (e.type === "dragleave") setIsDragging(false);
        };
        const handleDrop = (e) => {
            e.preventDefault(); e.stopPropagation();
            setIsDragging(false);
            if (e.dataTransfer.files && e.dataTransfer.files[0]) onFileSelect({ target: { files: e.dataTransfer.files } });
        };
        return (
            <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} onClick={() => inputRef.current.click()}
                style={{ border: isDragging ? '2px dashed #4f46e5' : '2px dashed #e5e7eb', backgroundColor: isDragging ? '#f5f3ff' : '#f9fafb', borderRadius: '16px', padding: '40px 20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s ease', position: 'relative' }}>
                <input type="file" ref={inputRef} onChange={onFileSelect} accept={accept} style={{ display: 'none' }} />
                {isUploading ? <div style={{ color: '#4f46e5', fontWeight: 'bold' }}>⬆️ Enviando arquivo...</div> :
                    <> <div style={{ fontSize: '32px', marginBottom: '10px' }}>{contentType === 'Video' ? '🎬' : contentType === 'PPTX' ? '📊' : '📁'}</div>
                        <p style={{ margin: 0, color: '#4b5563', fontWeight: '600' }}>Arraste seu arquivo aqui ou <span style={{ color: '#4f46e5' }}>clique para buscar</span></p>
                        <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '5px' }}>Formatos aceitos: {accept}</p> </>}
            </div>
        );
    };

    const handleSave = async () => {
        const loadingToast = toast.loading("Salvando curso e módulos...");
        setLoading(true);
        try {
            const payload = { title, description, status, cover_image_url: coverImageUrl, available_formats: availableFormats, price: paymentType === 'Gratuito' ? 0 : price, preview_video_url: hasPreview ? previewVideoUrl : null, preview_seconds: hasPreview ? Number(previewSeconds) : 0 };
            let courseId = courseData?.id;
            if (isEdit) await api.put(`/courses/${courseId}`, payload);
            else {
                const res = await api.post('/courses', payload);
                courseId = res.data.id || res.data.courseId;
            }

            // 🚩 MAPEAMENTO FINAL PARA O BANCO
            const modulesToSave = modules.map((m, index) => ({
                id: String(m.id).includes('temp') ? null : m.id,
                title: m.title,
                module_order: index + 1, 
                content_type: typeToDB(m.content_type), // 'HTML' -> 'texto', 'Video' -> 'video_upload'
                content: m.content_value, // Manda para a coluna 'content' do banco
                has_quiz: m.has_quiz ? 1 : 0,
                quiz_questions: m.quiz_questions || []
            }));

            await api.put(`/courses/${courseId}/modules`, { modules: modulesToSave });
            toast.success("Curso e módulos salvos com sucesso!", { id: loadingToast });
            clearDraft();
            if (onCourseSaved) onCourseSaved();
            onClose();
        } catch (err) {
            console.error("Erro ao salvar:", err);
            toast.error('Erro ao salvar curso.', { id: loadingToast });
        } finally { setLoading(false); }
    };

    if (!show || dataLoading) return null;
    
    
return (
  <div className="modal-overlay" style={{
    position: 'fixed', top: '60px', left: 0, width: '100vw', height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', justifyContent: 'center',
    alignItems: 'center', zIndex: 9999, backdropFilter: 'blur(8px)', padding: '20px'
  }}>
    <div className="modal-container" style={{
      backgroundColor: '#fff', width: '100%', maxWidth: '1100px', 
      height: '95vh', borderRadius: '24px', display: 'flex', flexDirection: 'column', 
      overflow: 'hidden', position: 'relative', boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
    }}>
      
      {/* HEADER FIXO */}
      <div className="modal-header" style={{
        padding: '20px 30px', borderBottom: '1px solid #f0f0f0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: '#fff', zIndex: 10
      }}>
        <h2 style={{ fontSize: '22px', fontWeight: '900', color: '#111827' }}>
          {isEdit ? "✏️ Editar Curso" : "✨ Criar Novo Curso"}
        </h2>
        <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', padding: '8px', cursor: 'pointer' }}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* CONTEÚDO COM SCROLL ÚNICO */}
      <div className="modal-content" style={{ 
        padding: '30px', overflowY: 'auto', flex: 1, backgroundColor: '#fff', scrollBehavior: 'smooth'
      }}>
        
        {/* INFO BÁSICA */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px', marginBottom: '30px' }}>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#4b5563', marginBottom: '8px', textTransform: 'uppercase' }}>Título do Curso</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '2px solid #f3f4f6', outline: 'none', fontSize: '15px' }} />
          </div>
          
          <div style={{ gridColumn: 'span 2' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#4b5563', marginBottom: '8px', textTransform: 'uppercase' }}>Descrição do Curso</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '2px solid #f3f4f6', minHeight: '100px', outline: 'none' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#4b5563', marginBottom: '8px', textTransform: 'uppercase' }}>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '2px solid #f3f4f6', background: '#fff' }}>
              <option value="Rascunho">🟡 Rascunho</option>
              <option value="Publicado">🟢 Publicado</option>
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#4b5563', marginBottom: '8px', textTransform: 'uppercase' }}>Acesso</label>
              <select value={paymentType} onChange={e => setPaymentType(e.target.value)} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '2px solid #f3f4f6' }}>
                <option value="Gratuito">Gratuito</option>
                <option value="Pago">Pago</option>
              </select>
            </div>
            {paymentType === 'Pago' && (
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', color: '#4b5563', marginBottom: '8px', textTransform: 'uppercase' }}>Preço</label>
                <input type="number" value={price} onChange={e => setPrice(e.target.value)} style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '2px solid #f3f4f6' }} placeholder="R$ 0,00" />
              </div>
            )}
          </div>
        </div>

        {/* DEGUSTAÇÃO E CAPA */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px', marginBottom: '40px' }}>
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
              <span style={{ fontWeight: '900', fontSize: '14px' }}>🎬 Degustação</span>
              <button onClick={() => setHasPreview(!hasPreview)} style={{ padding: '4px 12px', fontSize: '10px', borderRadius: '8px', border: 'none', background: hasPreview ? '#4f46e5' : '#d1d5db', color: '#fff', cursor: 'pointer' }}>
                {hasPreview ? 'ATIVO' : 'DESATIVADO'}
              </button>
            </div>
            {hasPreview && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <UploadDropzone 
                  accept="video/*" 
                  onFileSelect={handlePreviewUpload} 
                  isUploading={isPreviewUploading}
                  contentType="Video"
                />
                {previewVideoUrl && (
                  <div style={{ width: '100%', borderRadius: '12px', overflow: 'hidden', background: '#000', height: '120px', marginTop: '10px' }}>
                    <video src={getFullUrl(previewVideoUrl)} controls style={{ width: '100%', height: '100%' }} />
                  </div>
                )}
                <input type="number" placeholder="Segundos de preview" value={previewSeconds} onChange={e => setPreviewSeconds(e.target.value)} style={{ padding: '10px', borderRadius: '10px', border: '1px solid #ddd', marginTop: '5px' }} />
              </div>
            )}
          </div>
          
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
            <span style={{ fontWeight: '900', fontSize: '14px', display: 'block', marginBottom: '15px' }}>🖼️ Capa do Curso</span>
            <UploadDropzone 
              accept="image/*" 
              onFileSelect={handleCoverUpload} 
              isUploading={isCoverUploading}
              contentType="HTML"
            />
            {coverImageUrl && (
              <div style={{ width: '100%', height: '80px', borderRadius: '12px', overflow: 'hidden', marginTop: '10px', border: '1px solid #ddd' }}>
                <img src={getFullUrl(coverImageUrl)} alt="Capa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
          </div>
        </div>

        {/* MÓDULOS */}
        <div style={{ marginTop: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ fontWeight: '900', fontSize: '18px' }}>Módulos e Aulas</h3>
            <button onClick={addModule} style={{ background: '#4f46e5', color: '#fff', padding: '10px 20px', borderRadius: '12px', border: 'none', fontWeight: '800', cursor: 'pointer' }}>+ Add Módulo</button>
          </div>

          <div style={{ display: 'flex', gap: '15px', overflowX: 'auto', padding: '15px 5px', borderBottom: '2px solid #f3f4f6', marginBottom: '20px' }}>
            {modules.map((m, i) => (
              <div key={m.id || `mod-${i}`} style={{ position: 'relative', flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => setActiveTab(i)}
                  style={{ 
                    padding: '10px 25px', borderRadius: '12px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                    background: activeTab === i ? '#4f46e5' : '#f3f4f6', 
                    color: activeTab === i ? '#fff' : '#6b7280',
                    fontWeight: '800', transition: '0.3s'
                  }}
                >
                  {m.title || `Módulo ${i+1}`}
                </button>
                <button 
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeModule(m.id); }}
                  style={{ 
                    position: 'absolute', top: '-8px', right: '-8px', background: '#ef4444', color: '#fff', 
                    border: 'none', borderRadius: '50%', width: '22px', height: '22px', fontSize: '10px', 
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2
                  }}
                >✕</button>
              </div>
            ))}
          </div>

          {modules[activeTab] && (
          <div style={{ background: '#fff', borderRadius: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '5px' }}>Título do Módulo</label>
                <input type="text" value={modules[activeTab].title} onChange={e => handleModuleField(activeTab, 'title', e.target.value)} placeholder="Ex: Introdução ao curso" style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '2px solid #f3f4f6', outline: 'none' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '5px' }}>Tipo de Conteúdo</label>
                <select value={modules[activeTab].content_type} onChange={e => handleModuleField(activeTab, 'content_type', e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '2px solid #f3f4f6', background: '#fff' }}>
                  {contentTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
        
            <div style={{ marginBottom: '30px' }}>
              {modules[activeTab].content_type === 'HTML' ? (
                <div style={{ border: '2px solid #f3f4f6', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={modules[activeTab].content_value}
                    config={{
                      placeholder: 'Comece a escrever o conteúdo incrível do seu módulo...',
                      toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|', 'blockQuote', 'insertTable', 'mediaEmbed', '|', 'undo', 'redo'],
                    }}
                    onReady={editor => {
                      editor.editing.view.change(writer => {
                        writer.setStyle('min-height', '300px', editor.editing.view.document.getRoot());
                        writer.setStyle('padding', '20px', editor.editing.view.document.getRoot());
                      });
                    }}
                    onChange={(e, editor) => handleModuleField(activeTab, 'content_value', editor.getData())}
                  />
                </div>
              ) : modules[activeTab].content_type === 'YouTube' ? (
                /* IMPLEMENTAÇÃO YOUTUBE: INPUT EM VEZ DE DROPZONE */
                <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '20px', border: '2px solid #e2e8f0' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '800', marginBottom: '10px', color: '#4b5563' }}>URL DO VÍDEO (YOUTUBE)</label>
                  <input 
                    type="text" 
                    value={modules[activeTab].content_value} 
                    onChange={e => handleModuleField(activeTab, 'content_value', e.target.value)}
                    placeholder="Cole o link aqui (ex: https://www.youtube.com/watch?v=...)"
                    style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '2px solid #cbd5e1', outline: 'none' }}
                  />
                  {modules[activeTab].content_value && (
                    <p style={{ marginTop: '10px', fontSize: '12px', color: '#4f46e5', fontWeight: 'bold' }}>✅ Link configurado com sucesso</p>
                  )}
                </div>
              ) : (
                /* IMPLEMENTAÇÃO ARQUIVOS: INCLUINDO PPTX NO DROPZONE */
                <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '20px' }}>
                  <UploadDropzone 
                    accept={
                      modules[activeTab].content_type === 'Video' ? 'video/*' : 
                      modules[activeTab].content_type === 'PDF' ? '.pdf' : 
                      modules[activeTab].content_type === 'PPTX' ? '.pptx,.ppt' : 
                      modules[activeTab].content_type === 'Word' ? '.doc,.docx' : '*'
                    }
                    onFileSelect={handleFileUpload}
                    isUploading={modules[activeTab].isUploading}
                    contentType={modules[activeTab].content_type}
                  />
                            
                    {modules[activeTab].content_value && (
                    <div style={{ marginTop: '20px', padding: '15px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      {(() => {
                        const url = getFullUrl(modules[activeTab].content_value);
                        const type = modules[activeTab].content_type;
                        if (type === 'Video') return <video src={url} controls style={{ maxWidth: '100%', borderRadius: '8px', maxHeight: '300px' }} />;
                        if (type === 'PDF') return (
                          <div style={{ height: '400px', border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
                            <iframe src={`${url}#toolbar=0`} width="100%" height="100%" />
                          </div>
                        );
                        // NOVO: IMPLEMENTAÇÃO PARA APRESENTAÇÃO DE PPTX
                          if (type === 'PPTX') return (
                            <div style={{ height: '500px', border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
                              <iframe 
                                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`} 
                                width="100%" 
                                height="100%" 
                                frameBorder="0"
                              >
                                Esta é uma apresentação incorporada do <a target="_blank" href="https://office.com">Microsoft Office</a>, fornecida pelo <a target="_blank" href="https://office.com/webapps">Office Online</a>.
                              </iframe>
                            </div>
                          );
                        return (
                          <a href={url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', color: '#4f46e5', fontWeight: '700' }}>
                            <span>{type === 'PPTX' ? '📊' : '🔗'}</span> Visualizar arquivo: {modules[activeTab].content_value.split('/').pop()}
                          </a>
                        );
                      })()}
                    </div>
                    )}
                  </div>
                )}
              </div>

              {/* QUIZ DA UNIDADE */}
              <div style={{ background: '#f9fafb', padding: '25px', borderRadius: '24px', border: '2px solid #f3f4f6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <span style={{ fontWeight: '900' }}>📝 Quiz da Unidade</span>
                  <button onClick={() => handleQuizToggle(activeTab, !modules[activeTab].has_quiz)} style={{ background: modules[activeTab].has_quiz ? '#fee2e2' : '#dcfce7', color: modules[activeTab].has_quiz ? '#ef4444' : '#22c55e', border: 'none', padding: '8px 15px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer' }}>
                    {modules[activeTab].has_quiz ? 'Remover Quiz' : 'Ativar Quiz'}
                  </button>
                </div>

                {modules[activeTab].has_quiz && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {modules[activeTab].quiz_questions.map((q, qIdx) => (
                      <div key={qIdx} style={{ background: '#fff', padding: '20px', borderRadius: '15px', border: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <span style={{ fontSize: '12px', fontWeight: '900', color: '#4f46e5' }}>QUESTÃO {qIdx + 1}</span>
                          <button onClick={() => handleRemoveQuestion(activeTab, qIdx)} style={{ color: '#ef4444', border: 'none', background: 'none', fontWeight: 'bold', cursor: 'pointer' }}>Excluir</button>
                        </div>
                        <input type="text" value={q.question_text} onChange={e => handleQuestionFieldChange(activeTab, qIdx, 'question_text', e.target.value)} style={{ width: '100%', padding: '10px', border: 'none', borderBottom: '2px solid #f3f4f6', marginBottom: '15px', fontWeight: '700', outline: 'none' }} placeholder="Digite a pergunta aqui..." />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          {q.options.map((opt, oIdx) => (
                            <div key={oIdx} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: q.correct_option_index === oIdx ? '#f0fdf4' : '#f9fafb', padding: '10px', borderRadius: '10px', border: q.correct_option_index === oIdx ? '1px solid #22c55e' : '1px solid #eee' }}>
                              <input type="radio" name={`correct-${activeTab}-${qIdx}`} checked={q.correct_option_index === oIdx} onChange={() => handleQuestionFieldChange(activeTab, qIdx, 'correct_option_index', oIdx)} />
                              <input type="text" value={opt} onChange={e => handleQuestionFieldChange(activeTab, qIdx, 'option_text', { optionIndex: oIdx, text: e.target.value })} style={{ border: 'none', background: 'transparent', fontSize: '13px', width: '100%', outline: 'none' }} placeholder={`Opção ${optionLetters[oIdx]}`} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    <button onClick={() => handleAddQuestion(activeTab)} style={{ padding: '15px', border: '2px dashed #d1d5db', background: 'none', borderRadius: '15px', color: '#9ca3af', fontWeight: '800', cursor: 'pointer' }}>+ Nova Pergunta</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* FOOTER FIXO */}
      <div className="modal-footer" style={{ padding: '20px 30px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '15px', background: '#fff' }}>
        <button onClick={onClose} style={{ padding: '12px 25px', borderRadius: '12px', border: '2px solid #f3f4f6', background: '#fff', fontWeight: '800', color: '#6b7280', cursor: 'pointer' }}>Cancelar</button>
        <button onClick={handleSave} disabled={loading} style={{ padding: '12px 35px', borderRadius: '12px', border: 'none', background: '#111827', color: '#fff', fontWeight: '800', cursor: 'pointer' }}>
          {loading ? 'Salvando...' : '🔥 Salvar Curso'}
        </button>
      </div>
    </div>
  </div>
);
}

export default CourseFormModalTabs;